// VisualizerWithLegend.jsx
import React, { useRef, useState, useEffect, Suspense } from 'react';
import cytoscape from 'cytoscape';
import cytoscapePopper from 'cytoscape-popper';
import { createPopper } from '@popperjs/core';
import tippy from 'tippy.js';
import 'tippy.js/dist/tippy.css';
import CytoscapeComponent from 'react-cytoscapejs';

import { useTransactions } from '../hooks/useTransactions';
import { VisualizerNavbar } from '../components/visualizer/Navbar';
import { PillsBar } from '../components/visualizer/PillsBar';
import { RightSidebar } from '../components/visualizer/RightSidebar';
import { LoadingSkeleton } from '../components/visualizer/LoadingSkeleton';
import { Footer } from '../components/visualizer/Footer';
import { Breadcrumbs, ContentLayout } from 'components/Layout';
import { Head } from 'components/Head';
import { ChevronDown } from 'lucide-react';

cytoscape.use(cytoscapePopper(createPopper));

const TYPE_COLORS = {
  deposits: '#43B929',
  withdraws: '#E64A19',
  transfers: '#1E88E5',
  swaps: '#FFC107',
};

const Legend = ({ types, onClick }) => (
  <div className="absolute top-20 left-6 z-40 bg-black/50 backdrop-blur rounded p-2 text-white flex space-x-4">
    {types.map((t) => (
      <div
        key={t}
        onClick={() => onClick(t)}
        className="flex items-center cursor-pointer"
      >
        <div
          style={{ backgroundColor: TYPE_COLORS[t] }}
          className="w-4 h-4 mr-1 rounded"
        />
        <span className="text-sm">{t}</span>
      </div>
    ))}
  </div>
);

export const Visualizer = () => {
  const {
    searchResults,
    activeCurrency,
    handleSearch,
    setActiveCurrency,
    transactions,
  } = useTransactions();
  const [elements, setElements] = useState([]);
  const cyRef = useRef(null);

  // Build nodes and edges from transactions hook
  useEffect(() => {
    const all = [];
    if (!activeCurrency) {
      setElements([]);
      return;
    }
    const userId = activeCurrency.id.toString();
    all.push({
      data: { id: userId, label: userId },
    });

    const { deposits, withdraws, swaps, transfers } = transactions;

    const mapping = {
      deposits: Array.isArray(deposits) ? deposits : [],
      withdraws: Array.isArray(withdraws) ? withdraws : [],
      swaps: Array.isArray(swaps) ? swaps : [],
      transfers: [
        ...(Array.isArray(transactions.transfers?.transactions?.successful)
          ? transactions.transfers.transactions.successful
          : []),
        ...(Array.isArray(transactions.transfers?.transactions?.failed)
          ? transactions.transfers.transactions.failed
          : []),
      ],
    };

    Object.entries(mapping).forEach(([type, txs]) => {
      console.log(txs);
      txs.forEach((tx) => {
        const txId = `${type}-${tx.id}`;
        all.push({
          data: {
            id: txId,
            label: trimId(tx.id),
            group: type,
            groupColor: TYPE_COLORS[type],
          },
        });
        all.push({
          data: {
            source: userId,
            target: txId,
            label: type,
          },
        });
      });
    });
    setElements(all);
  }, [activeCurrency, transactions]);

  const style = [
    {
      selector: 'node',
      style: {
        'background-color': 'data(groupColor)',
        label: 'data(label)',
        color: '#fff',
        'text-outline-width': 2,
        'text-outline-color': '#000',
      },
    },
    {
      selector: '.highlighted',
      style: {
        'border-width': 4,
        'border-color': '#fff',
        'shadow-blur': 20,
        'shadow-color': '#fff',
        'shadow-opacity': 0.7,
      },
    },
    {
      selector: 'edge',
      style: {
        'curve-style': 'bezier',
        'line-color': '#888',
        'target-arrow-color': '#888',
        'target-arrow-shape': 'triangle',
        'arrow-scale': 0.5,
        label: 'data(label)',
        'font-size': '10px',
        color: '#ccc',
      },
    },
  ];

  const handleLegendClick = (type) => {
    const cy = cyRef.current;
    if (!cy) return;
    cy.nodes().removeClass('highlighted');
    cy.nodes(`[group = "${type}"]`).addClass('highlighted');
  };

  return (
    <ContentLayout
      title={
        <>
          <h2 className="text-2xl font-semibold text-neutral-100">
            Visualizer
          </h2>
          <h2 className="text-sm font-light text-neutral-100">
            Explore transactional relationships and data flows across Wire24.{' '}
            <button onClick={() => setShowMore(true)} className="underline">
              Learn more
            </button>
          </h2>
        </>
      }
      breadcrumbs={<Breadcrumbs current="Visualizer" />}
      end={
        <button className="flex flex-row items-center space-x-2">
          <span>Saved Graphs</span>
          <ChevronDown />
        </button>
      }
    >
      <Head title="Transactions | Visualizer" />
      <div className="h-[70vh] rounded-3xl bg-black/10 backdrop-blur-sm flex flex-col text-white relative">
        <VisualizerNavbar
          onSearch={handleSearch}
          onSelectCurrency={setActiveCurrency}
          searchResults={searchResults}
          activeCurrency={activeCurrency}
        />
        <PillsBar
          users={activeCurrency ? [activeCurrency] : []}
          onRemove={() => setActiveCurrency(null)}
          sortByTime={false}
          onToggleSort={() => {}}
        />
        <Legend types={Object.keys(TYPE_COLORS)} onClick={handleLegendClick} />
        <div className="flex-1 relative">
          <Suspense fallback={<LoadingSkeleton />}>
            <CytoscapeComponent
              elements={elements}
              stylesheet={style}
              style={{ width: '100%', height: '100%' }}
              layout={{ name: 'cose', animate: true }}
              cy={(cy) => {
                cyRef.current = cy;
                cy.on('mouseover', 'node', (e) => {
                  const node = e.target;
                  const popper = node.popper({
                    content: () => {
                      const el = document.createElement('div');
                      el.textContent = node.id();
                      document.body.appendChild(el);
                      return el;
                    },
                  });
                  const tip = tippy(document.createElement('div'), {
                    getReferenceClientRect:
                      node.popperRef().getBoundingClientRect,
                    content: node.id(),
                    trigger: 'manual',
                    placement: 'top',
                  });
                  tip.show();
                  const update = () => popper.update();
                  node.on('position', update);
                  cy.on('pan zoom resize', update);
                  node.on('mouseout', () => {
                    tip.destroy();
                    popper.destroy && popper.destroy();
                    node.off('position', update);
                    cy.off('pan zoom resize', update);
                  });
                });
              }}
            />
          </Suspense>
          <RightSidebar />
        </div>
        <Footer />
      </div>
    </ContentLayout>
  );
};

const trimId = (idStr) =>
  idStr.length > 8 ? idStr.slice(0, 6) + '...' : idStr;
